<script lang="ts">
  import type {
    Project,
    ProjectsByStatus,
    ProjectStatus,
    CalendarEvent,
    DisplayFilter,
  } from '../lib/types';
  import { getMonthDisplayName, isCurrentMonth, getLocalToday } from '../lib/utils';
  import { openProjectModalForCreate, requestProjectMove } from '../lib/stores/ui';
  import { useExpandableProjects } from '../lib/useExpandableProjects.svelte';
  import { useProjectDragDrop } from '../lib/useProjectDragDrop.svelte';
  import ExpandableProjectChip from './ExpandableProjectChip.svelte';
  import { viewport } from '../lib/stores/viewport';
  import MobileFocusView from './mobile/MobileFocusView.svelte';

  interface Props {
    visibleMonths: string[]; // Unused - kept for Dashboard API compatibility
    projectsByMonth: Record<string, ProjectsByStatus>;
    events: CalendarEvent[]; // Unused - Focus view is project-only
    currentMonth: string;
    display?: DisplayFilter | null; // Unused - Focus view always shows projects
    headerLabel?: string | null; // Optional override for the header title
    hideWeekPills?: boolean; // Hide week pills (e.g., for year scale)
  }

  let {
    projectsByMonth,
    currentMonth,
    headerLabel = null,
    hideWeekPills = false,
  }: Props = $props();

  // Status configuration
  const statusConfig: { key: ProjectStatus; label: string; icon: string; accent: string }[] = [
    { key: 'planned', label: 'Planned', icon: '📋', accent: 'blue' },
    { key: 'active', label: 'Active', icon: '🔄', accent: 'amber' },
    { key: 'done', label: 'Done', icon: '✅', accent: 'green' },
  ];

  // Get projects by status for current month
  // Note: "almost" status projects are included with "active" (semantically still in-progress)
  function getProjects(status: ProjectStatus): Project[] {
    const data = projectsByMonth[currentMonth] || { planned: [], active: [], almost: [], done: [] };
    if (status === 'active') {
      return [...(data.active || []), ...(data.almost || [])];
    }
    return data[status] || [];
  }

  // Get all projects for current month
  function getAllProjects(): Project[] {
    const data = projectsByMonth[currentMonth] || { planned: [], active: [], almost: [], done: [] };
    return [
      ...(data.active || []),
      ...(data.almost || []),
      ...(data.planned || []),
      ...(data.done || []),
    ];
  }

  // Get weeks of the month for display
  function getMonthWeeks(): {
    weekNum: number;
    startDay: number;
    endDay: number;
    isCurrent: boolean;
  }[] {
    const [year, month] = currentMonth.split('-').map(Number);
    const firstDay = new Date(Date.UTC(year, month - 1, 1));
    const lastDay = new Date(Date.UTC(year, month, 0));
    const today = getLocalToday();

    const weeks: { weekNum: number; startDay: number; endDay: number; isCurrent: boolean }[] = [];
    let currentDate = new Date(firstDay);

    // Adjust to start of week (Sunday)
    const dayOfWeek = currentDate.getUTCDay();
    currentDate.setUTCDate(currentDate.getUTCDate() - dayOfWeek);

    while (true) {
      if (!(currentDate <= lastDay || currentDate.getUTCMonth() === month - 1)) break;
      const weekStart = new Date(currentDate);
      const weekEnd = new Date(currentDate);
      weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);

      // Get week number
      const tempDate = new Date(currentDate);
      tempDate.setUTCDate(tempDate.getUTCDate() + 4 - (tempDate.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
      const weekNum = Math.ceil(((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

      // Check if current week
      const isCurrent = today >= weekStart && today <= weekEnd;

      // Get start and end days that fall within the month
      const startDay = weekStart.getUTCMonth() === month - 1 ? weekStart.getUTCDate() : 1;
      const endDay =
        weekEnd.getUTCMonth() === month - 1 ? weekEnd.getUTCDate() : lastDay.getUTCDate();

      weeks.push({ weekNum, startDay, endDay, isCurrent });

      currentDate.setUTCDate(currentDate.getUTCDate() + 7);

      // Stop if we've moved past the month
      if (
        currentDate.getUTCMonth() > month - 1 ||
        (currentDate.getUTCMonth() === 0 && month === 12)
      ) {
        break;
      }
    }

    return weeks.slice(0, 6); // Max 6 weeks in a month view
  }

  function handleAddProject(status: ProjectStatus = 'planned') {
    openProjectModalForCreate({
      schedulingState: 'scheduled',
      monthKey: currentMonth,
      status: status,
    });
  }

  // Expand/collapse state (shared utility)
  const expander = useExpandableProjects(() => getAllProjects());
  // eslint-disable-next-line momentium/no-unguarded-effect -- idempotent expander init: runs once via hasInitialized guard, needs reactive project data
  $effect(() => expander.initializeExpansion());

  // Derived: are all projects globally expanded?
  let allExpanded = $derived(() => expander.areAllExpanded(getAllProjects()));

  // Drag and drop state (shared utility)
  const dnd = useProjectDragDrop((projectId, project, status) => {
    requestProjectMove(projectId, project, 'month', { monthKey: currentMonth, status });
  });
</script>

{#if $viewport.isMobile}
  <MobileFocusView
    {getProjects}
    periods={hideWeekPills
      ? []
      : getMonthWeeks().map((week) => ({
          id: String(week.weekNum),
          label: `W${week.weekNum} · ${week.startDay}–${week.endDay}`,
          active: week.isCurrent,
        }))}
    onAddProject={handleAddProject}
  />
{:else}
  <div class="focus-container">
    <!-- Header -->
    <header class="focus-header">
      <div class="header-left">
        <h1 class="month-title">{headerLabel || getMonthDisplayName(currentMonth)}</h1>
        {#if !headerLabel && isCurrentMonth(currentMonth)}
          <span class="current-badge">This Month</span>
        {/if}
      </div>

      <div class="header-right">
        {#if getAllProjects().length > 0}
          <button
            type="button"
            class="expand-all-btn"
            class:all-expanded={allExpanded()}
            onclick={() =>
              allExpanded() ? expander.collapseAll() : expander.expandGroup(getAllProjects())}
            title={allExpanded() ? 'Collapse all projects' : 'Expand all projects'}
          >
            <span class="expand-all-icon">{allExpanded() ? '▼' : '▶'}</span>
            <span class="expand-all-text">{allExpanded() ? 'Collapse' : 'Expand'}</span>
          </button>
        {/if}

        {#if !hideWeekPills}
          <div class="week-pills">
            {#each getMonthWeeks() as week}
              <div class="week-pill" class:current={week.isCurrent}>
                <span class="week-label">W{week.weekNum}</span>
                <span class="week-days">{week.startDay}-{week.endDay}</span>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    </header>

    <!-- Kanban Board -->
    <div class="kanban-board">
      {#each statusConfig as status}
        {@const projects = getProjects(status.key)}
        {@const allExpanded = expander.areAllExpanded(projects)}

        <div
          class="kanban-column column-{status.accent}"
          class:drop-target={dnd.dropTarget === status.key}
          role="group"
          aria-label="{status.label} projects"
          ondragover={(e) => dnd.handleDragOver(e, status.key)}
          ondragleave={dnd.handleDragLeave}
          ondrop={(e) => dnd.handleDrop(e, status.key)}
        >
          <div class="column-header">
            <span class="column-icon">{status.icon}</span>
            <span class="column-title">{status.label}</span>
            <span class="column-count">{projects.length}</span>

            {#if projects.length > 0}
              <button
                type="button"
                class="expand-toggle-btn"
                class:all-expanded={allExpanded}
                onclick={() =>
                  allExpanded ? expander.collapseGroup(projects) : expander.expandGroup(projects)}
                title={allExpanded ? 'Collapse all' : 'Expand all'}
              >
                {allExpanded ? '▼' : '▶'}
              </button>
            {/if}

            <button
              type="button"
              class="add-btn"
              onclick={() => handleAddProject(status.key)}
              title="Add project to {status.label}"
            >
              +
            </button>
          </div>

          <div class="column-content {status.key !== 'done' ? 'wide-column' : ''}">
            {#each projects as project (project.id)}
              <ExpandableProjectChip
                {project}
                expanded={expander.isProjectExpanded(project.id)}
                onToggleExpand={() => expander.toggleProjectExpand(project.id)}
                isDragging={dnd.draggingProjectId === project.id}
                onDragStart={(e) => dnd.handleDragStart(e, project, status.key)}
                onDragEnd={dnd.handleDragEnd}
              />
            {/each}

            {#if projects.length === 0}
              <div class="column-empty">
                <span class="empty-icon">{status.icon}</span>
                <span class="empty-text">No {status.label.toLowerCase()} projects</span>
                <button
                  type="button"
                  class="empty-add-btn"
                  onclick={() => handleAddProject(status.key)}
                >
                  + Add project
                </button>
              </div>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  </div>
{/if}

<style>
  .focus-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0.75rem;
    gap: 0.75rem;
    overflow: hidden;
    background: #f8fafc;
  }

  /* Header - compact */
  .focus-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.625rem;
    background: white;
    border-radius: 0.375rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    flex-shrink: 0;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .month-title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    letter-spacing: -0.01em;
  }

  .current-badge {
    font-size: 0.5625rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    padding: 0.125rem 0.375rem;
    border-radius: 0.1875rem;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .expand-all-btn {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #64748b;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .expand-all-btn:hover {
    color: #3b82f6;
    border-color: #93c5fd;
    background: #eff6ff;
  }

  .expand-all-btn.all-expanded {
    color: #3b82f6;
    background: #eff6ff;
    border-color: #93c5fd;
  }

  .expand-all-icon {
    font-size: 0.5rem;
  }

  .expand-all-text {
    line-height: 1;
  }

  /* Week pills - compact */
  .week-pills {
    display: flex;
    gap: 0.1875rem;
  }

  .week-pill {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.125rem 0.375rem;
    background: #f8fafc;
    border-radius: 0.25rem;
    min-width: 2.5rem;
    transition: all 0.15s;
  }

  .week-pill.current {
    background: #dbeafe;
    box-shadow: inset 0 0 0 1.5px #3b82f6;
  }

  .week-label {
    font-size: 0.5rem;
    font-weight: 600;
    color: #94a3b8;
    letter-spacing: 0.02em;
  }

  .week-pill.current .week-label {
    color: #3b82f6;
  }

  .week-days {
    font-size: 0.625rem;
    font-weight: 600;
    color: #334155;
  }

  .week-pill.current .week-days {
    color: #1d4ed8;
  }

  /* Kanban Board */
  .kanban-board {
    display: flex;
    gap: 0.75rem;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .kanban-column {
    flex: 2; /* Default: 2/5 width */
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.375rem;
    overflow: hidden;
    transition: all 0.15s;
    min-width: 0;
    min-height: 0;
    position: relative;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  /* Done column is narrower: 1/5 width */
  .column-green {
    flex: 1;
  }

  /* Column accent bar - flat top edge */
  .kanban-column::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--column-accent);
  }

  .column-blue {
    --column-accent: #3b82f6;
  }

  .column-amber {
    --column-accent: #f59e0b;
  }

  .column-green {
    --column-accent: #22c55e;
  }

  .kanban-column.drop-target {
    background: #eff6ff !important;
    box-shadow:
      inset 0 0 0 2px #3b82f6,
      0 4px 12px rgba(59, 130, 246, 0.15) !important;
  }

  /* Column header - compact */
  .column-header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;
    padding-top: 0.5rem;
    border-bottom: 1px solid #f1f5f9;
    flex-shrink: 0;
  }

  .column-icon {
    font-size: 0.75rem;
  }

  .column-title {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #334155;
  }

  .column-count {
    font-size: 0.5625rem;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    padding: 0.0625rem 0.3125rem;
    border-radius: 0.1875rem;
    min-width: 1rem;
    text-align: center;
  }

  .expand-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.125rem;
    height: 1.125rem;
    margin-left: auto;
    font-size: 0.5rem;
    color: #94a3b8;
    background: none;
    border: 1px solid #e2e8f0;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .expand-toggle-btn:hover {
    color: #3b82f6;
    border-color: #93c5fd;
    background: #eff6ff;
  }

  .expand-toggle-btn.all-expanded {
    color: #3b82f6;
    border-color: #93c5fd;
    background: #eff6ff;
  }

  .add-btn {
    width: 1.125rem;
    height: 1.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 500;
    color: #94a3b8;
    background: none;
    border: 1px solid #e2e8f0;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .add-btn:hover {
    color: #3b82f6;
    border-color: #93c5fd;
    background: #eff6ff;
  }

  /* Column content - scrollable grid area */
  .column-content {
    display: grid;
    grid-template-columns: 1fr; /* Default: single column (Done + mobile) */
    grid-auto-rows: min-content; /* Rows size to content, don't stretch */
    gap: 0.375rem;
    padding: 0.5rem;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    align-content: start;

    /* Subtle scrollbar */
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }

  /* Wide columns (Planned/Active) get 2 columns by default */
  .column-content.wide-column {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Below 1280px - single column cards and equal width lanes */
  @media (max-width: 1280px) {
    .column-content.wide-column {
      grid-template-columns: 1fr;
    }

    .kanban-column {
      flex: 1; /* Equal 1/3 width for all columns */
    }
  }

  .column-content::-webkit-scrollbar {
    width: 6px;
  }

  .column-content::-webkit-scrollbar-track {
    background: transparent;
  }

  .column-content::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  .column-content::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  /* Expandable chips in grid - stay in single column even when expanded */
  .column-content :global(.expandable-chip) {
    min-width: 0;
    width: auto;
  }

  /* When column is empty, center the empty state */
  .column-content:has(.column-empty) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Empty state */
  .column-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    max-width: 12rem;
    padding: 1.5rem 1rem;
    text-align: center;
    color: #94a3b8;
    border: 1.5px dashed #e2e8f0;
    border-radius: 0.375rem;
    background: #fafbfc;
  }

  .empty-icon {
    font-size: 1.5rem;
    opacity: 0.5;
  }

  .empty-text {
    font-size: 0.75rem;
    font-weight: 500;
  }

  .empty-add-btn {
    margin-top: 0.25rem;
    padding: 0.375rem 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #3b82f6;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .empty-add-btn:hover {
    background: #dbeafe;
    border-color: #93c5fd;
  }
</style>
